import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from './AIPage.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faRobot, faBolt, faTrash } from '@fortawesome/free-solid-svg-icons';
import { getAIResponse } from './aiResponses';

const TYPING_SPEED = 16; // ms per chunk
const CHUNK_SIZE = 3;    // chars per tick
const MAX_USAGE = 50;

const QUICK_REPLIES = [
    'Расскажи интересный факт 🤯',
    'Пошути 😄',
    'Что умеешь?',
    'Поговорим о жизни',
    'Мотивируй меня 💪',
];

const formatTime = (date) =>
    date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

const GREETINGS = [
    'Привет! Я Кочевник AI (Alpha). Ещё учусь, но уже готов ответить на вопросы и просто поболтать. Чем могу помочь?',
    'Салам! Я твой цифровой собеседник от NCORE. Могу рассказать факт, пошутить или поддержать разговор — спрашивай!',
    'Здравствуй! Мои алгоритмы настроены и готовы к работе. О чём поговорим?',
    'Привет! Я Кочевник AI. Задай мне любой вопрос или просто напиши что думаешь — разберёмся вместе.',
];

export default function AIPage() {
    const [messages, setMessages] = useState(() => {
        const text = GREETINGS[Math.floor(Math.random() * GREETINGS.length)];
        return [{ id: 1, type: 'ai', text, displayText: text, animating: false, time: new Date() }];
    });
    const [inputValue, setInputValue] = useState('');
    const [isThinking, setIsThinking] = useState(false);
    const [usageCount, setUsageCount] = useState(0);
    const [animatingId, setAnimatingId] = useState(null);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // ── Typewriter effect ──
    useEffect(() => {
        if (!animatingId) return;
        const msg = messages.find(m => m.id === animatingId);
        if (!msg || !msg.animating) return;

        if (msg.displayText.length >= msg.text.length) {
            setMessages(prev => prev.map(m =>
                m.id === animatingId ? { ...m, animating: false } : m
            ));
            setAnimatingId(null);
            return;
        }

        const timer = setTimeout(() => {
            setMessages(prev => prev.map(m => {
                if (m.id !== animatingId) return m;
                return { ...m, displayText: m.text.slice(0, m.displayText.length + CHUNK_SIZE) };
            }));
        }, TYPING_SPEED);

        return () => clearTimeout(timer);
    }, [animatingId, messages]);

    // ── Auto scroll ──
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isThinking]);

    const addAiMessage = useCallback((text) => {
        const id = Date.now() + 1;
        setMessages(prev => [...prev, {
            id, type: 'ai', text,
            displayText: '',
            animating: true,
            time: new Date(),
        }]);
        setAnimatingId(id);
    }, []);

    const handleSend = useCallback((text) => {
        const userText = (text || inputValue).trim();
        if (!userText || isThinking || animatingId) return;
        setInputValue('');

        const userMsg = { id: Date.now(), type: 'user', text: userText, displayText: userText, animating: false, time: new Date() };
        setMessages(prev => {
            const next = [...prev, userMsg];

            const nextUsage = usageCount + 1;
            setUsageCount(nextUsage);

            if (nextUsage > MAX_USAGE) {
                setTimeout(() => {
                    addAiMessage('⏳ Лимит запросов временно исчерпан. Мы в Alpha-тестировании и ресурсы пока ограничены. Каждый твой запрос помогает мне учиться — спасибо! Следите за обновлениями NCORE. 🚀');
                }, 600);
                return next;
            }

            setIsThinking(true);
            const delay = 600 + Math.random() * 800;
            setTimeout(() => {
                const response = getAIResponse(userText, next);
                setIsThinking(false);
                addAiMessage(response);
            }, delay);

            return next;
        });

        inputRef.current?.focus();
    }, [inputValue, isThinking, animatingId, usageCount, addAiMessage]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleClear = () => {
        const text = GREETINGS[Math.floor(Math.random() * GREETINGS.length)];
        setMessages([{ id: Date.now(), type: 'ai', text, displayText: text, animating: false, time: new Date() }]);
        setUsageCount(0);
        setAnimatingId(null);
        setIsThinking(false);
        inputRef.current?.focus();
    };

    const isLimited = usageCount >= MAX_USAGE;
    const canSend = inputValue.trim() && !isThinking && !animatingId && !isLimited;

    return (
        <div className={styles.container}>
            <div className={styles.chatWindow}>

                {/* ── Header ── */}
                <div className={styles.header}>
                    <div className={styles.title}>
                        <div className={styles.aiAvatarHeader}>
                            <FontAwesomeIcon icon={faRobot} />
                        </div>
                        <div>
                            <div className={styles.titleName}>Кочевник AI</div>
                            <div className={styles.titleSub}>{isThinking ? 'думает...' : 'online'}</div>
                        </div>
                        <span className={styles.status}>Alpha</span>
                    </div>
                    <div className={styles.headerRight}>
                        <div className={styles.limitBadge}>
                            <FontAwesomeIcon icon={faBolt} />
                            <span>{Math.max(0, MAX_USAGE - usageCount)}</span>
                        </div>
                        <button className={styles.clearBtn} onClick={handleClear} title="Очистить чат">
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    </div>
                </div>

                {/* ── Messages ── */}
                <div className={styles.messagesArea}>
                    {messages.map((msg) => (
                        <div key={msg.id} className={`${styles.messageWrapper} ${msg.type === 'user' ? styles.wrapperUser : styles.wrapperAi}`}>
                            {msg.type === 'ai' && (
                                <div className={styles.aiAvatar}>
                                    <FontAwesomeIcon icon={faRobot} />
                                </div>
                            )}
                            <div className={`${styles.message} ${msg.type === 'user' ? styles.userMessage : styles.aiMessage}`}>
                                <div className={styles.messageText}>
                                    {msg.displayText || ''}
                                    {msg.animating && <span className={styles.cursor} />}
                                </div>
                                <div className={styles.messageTime}>{formatTime(msg.time)}</div>
                            </div>
                        </div>
                    ))}

                    {isThinking && (
                        <div className={`${styles.messageWrapper} ${styles.wrapperAi}`}>
                            <div className={styles.aiAvatar}>
                                <FontAwesomeIcon icon={faRobot} />
                            </div>
                            <div className={styles.typingIndicator}>
                                <span /><span /><span />
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* ── Quick replies ── */}
                {!isThinking && !animatingId && !isLimited && (
                    <div className={styles.quickReplies}>
                        {QUICK_REPLIES.map((q) => (
                            <button key={q} className={styles.quickReply} onClick={() => handleSend(q)}>
                                {q}
                            </button>
                        ))}
                    </div>
                )}

                {/* ── Input ── */}
                <div className={styles.inputArea}>
                    <div className={styles.inputContainer}>
                        <input
                            ref={inputRef}
                            type="text"
                            className={styles.input}
                            placeholder={isLimited ? 'Лимит исчерпан...' : 'Напиши что-нибудь...'}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={isLimited}
                            maxLength={500}
                            autoComplete="off"
                        />
                        <button
                            className={`${styles.sendButton} ${canSend ? styles.sendActive : ''}`}
                            onClick={() => handleSend()}
                            disabled={!canSend}
                        >
                            <FontAwesomeIcon icon={faPaperPlane} />
                        </button>
                    </div>
                    <div className={styles.inputFooter}>
                        Ответы генерируются ИИ и могут быть неточными&nbsp;·&nbsp;Alpha Preview
                    </div>
                </div>
            </div>
        </div>
    );
}
